<script setup>
import IconCoupon from 'assets/icons/coupon.svg?component'
import { computed } from 'vue'
import SkeletonPlaceholder from '@/components/Base/SkeletonPlaceholder.vue'

const props = defineProps({
    shop: {
        type: Object,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                uid: rawValue => rawValue.uid === undefined || typeof rawValue.uid === 'number',
                type: rawValue => rawValue.type === undefined || typeof rawValue.type === 'string',
                name: rawValue => rawValue.name === undefined || typeof rawValue.name === 'string',
                thumbnail: rawValue => rawValue.logotype?.thumbnail === undefined || typeof rawValue.logotype.thumbnail === 'string',
                altText: rawValue => rawValue.logotype?.altText === undefined || typeof rawValue.logotype.altText === 'string',
                hasLoyaltyProgram: rawValue => rawValue.hasLoyaltyProgram === undefined || typeof rawValue.hasLoyaltyProgram === 'boolean',
                hasCoupons: rawValue => rawValue.hasCoupons === undefined || typeof rawValue.hasCoupons === 'boolean',
                loyaltyProgramName: rawValue => rawValue.loyaltyProgram?.name === undefined || typeof rawValue.loyaltyProgram.name === 'string',
                loyaltyDescriptionShort: rawValue => rawValue.loyaltyProgram?.descriptionShort === undefined || typeof rawValue.loyaltyProgram.descriptionShort === 'string',
                placeholder: rawValue => rawValue?.placeholder === undefined || typeof rawValue.placeholder === 'boolean'
            }

            return Object.entries(validations).every(([_, validate]) => validate(rawValue))
        }
    }
})

// Define refs
const shopListItem = ref(null)
const shopListLoyalty = ref(null)
const loyaltyShortDescriptionTruncated = ref(null)
const distanceFromBottom = ref(0)

// Get locale path
const localePath = useLocalePath()

// Fetch route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Get center details
const { data: centerDetails, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)

onServerPrefetch(async () => {
    await centerDetailSuspense()
})

// Markdown
const { $md } = useNuxtApp()
const loyaltyHeadline = computed(() => $md.render(props.shop?.loyaltyProgram?.name || ''))
const loyaltyShortDescription = computed(() => $md.render(props.shop?.loyaltyProgram?.descriptionShort || ''))
const getLoyaltyShortDescription = computed(() => loyaltyShortDescriptionTruncated.value ? loyaltyShortDescriptionTruncated.value : loyaltyShortDescription.value)

const getShopItemClass = computed(() => {
    return {
        'shop-list-item-loyalty': props.shop?.hasLoyaltyProgram
    }
})

const shopItemLink = computed(() => {
    return localePath({
        name: 'centerUid-shops-shopUid',
        params: { shopUid: props.shop.uid }
    })
})

/**
 * Tracks the user's interaction with the shop feature.
 *
 * @return {void} This function does not return a value.
 */
const trackShop = () => {
    const pageName = route?.meta?.pageName

    if (!pageName || props.shop.placeholder) {
        return
    }

    const trackingPayload = {
        center: {
            centerUid: centerDetails.value?.centerUid,
            centerName: centerDetails.value?.centerName,
            shopId: props.shop?.name,
            screenName: 'Home_Page'
        },
        centerShops: {
            centerUid: centerDetails.value?.centerUid,
            shopName: props.shop?.name,
            screenName: 'Shops_Overview'
        }
    }

    // Track user click
    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.SHOP_CLICK,
        ...trackingPayload[pageName]
    })
}

const itemComponent = computed(() => props?.shop?.placeholder ? 'DefaultContentBox' : resolveComponent('NuxtLinkLocale'))

// Prevent expensive calculations
const isTruncationCalculated = useState('isTruncationCalculated', () => false)

// On mounted
onMounted(() => {
    if (isTruncationCalculated.value) {
        return // Skip logic
    }

    // Create an IntersectionObserver to detect when the element is in view
    if (import.meta.client) {
        const observer = new IntersectionObserver(async (entries) => {
            for (const entry of entries) {
                // Check if the observed element is intersecting with the viewport
                if (entry.isIntersecting) {
                    // Check if shopListLoyalty.value is truthy
                    if (shopListLoyalty.value) {
                        // Wait for the next DOM update to ensure accurate calculations
                        await nextTick()

                        // Calculate the initial distance from the bottom of shopListLoyalty
                        distanceFromBottom.value = shopListItem.value.clientHeight - shopListLoyalty.value?.offsetTop - shopListLoyalty.value?.clientHeight

                        // Assign the initial value to loyaltyShortDescriptionTruncated
                        loyaltyShortDescriptionTruncated.value = loyaltyShortDescription.value

                        // Check the distanceFromBottom in a while loop
                        while (distanceFromBottom.value < 25) {
                            // Assign a new value by truncating the description
                            loyaltyShortDescriptionTruncated.value = getFormattedList(loyaltyShortDescriptionTruncated.value)

                            // Wait for the next DOM update
                            await nextTick()

                            // Recalculate the distanceFromBottom
                            distanceFromBottom.value = shopListItem.value.clientHeight - shopListLoyalty.value.offsetTop - shopListLoyalty.value.clientHeight

                            // Exit the loop if distanceFromBottom is no longer less than 25
                            if (distanceFromBottom.value >= 25) {
                                break
                            }
                        }
                    }
                }

                isTruncationCalculated.value = true
            }
        }, {
            threshold: 0.5 // Trigger the callback when 50% of the element is visible
        })

        // Check if shopListItem is truthy and has the specified class before observing
        if (shopListItem.value && shopListItem.value.classList.contains('shop-list-item-loyalty')) {
            observer.observe(shopListItem.value)
        }
    }
})
</script>

<template>
    <li
        ref="shopListItem"
        class="shop-list-item"
        :class="getShopItemClass"
        :title="shop.name"
    >
        <component
            :is="itemComponent"
            class="shop-list-item-link"
            :to="shopItemLink"
            :class="{ 'skeleton-placeholder-container': shop.placeholder }"
            @click="trackShop()"
        >
            <IconCoupon
                v-if="shop.hasCoupons"
                class="shop-list-benefit-icon"
            />

            <div class="shop-list-item-thumbnail">
                <component
                    :is="SkeletonPlaceholder"
                    v-if="shop.placeholder"
                    :skeleton-placeholder-config="{ type: 'image', width: '130px', height: '90px' }"
                />

                <img
                    v-else
                    loading="lazy"
                    width="130"
                    height="130"
                    :src="shop?.logotype?.thumbnail"
                    :alt="shop?.name"
                >
            </div>

            <aside
                v-if="shop?.loyaltyProgram"
                ref="shopListLoyalty"
                class="loyalty-program-info"
            >
                <component
                    :is="SkeletonPlaceholder"
                    v-if="shop.placeholder"
                    :skeleton-placeholder-config="{ type: 'multi-headline', tag: 'h3' }"
                />
                <h3 v-else-if="shop.loyaltyProgram?.name" v-html="loyaltyHeadline" />

                <component
                    :is="SkeletonPlaceholder"
                    v-if="shop.placeholder"
                    :skeleton-placeholder-config="{ type: 'text', iterator: 2 }"
                />

                <div v-else-if="shop.loyaltyProgram?.descriptionShort" class="loyalty-program-info-list" v-html="getLoyaltyShortDescription" />
            </aside>
        </component>
    </li>
</template>

<style lang="scss">
.shop-list-item {
    grid-row: span 1;

    &.shop-list-item-loyalty {
        height: auto;
        grid-row: span 2;
    }
}

.shop-list-item-link {
    display: block;
    text-decoration: none;
    color: inherit;
    position: relative;
    padding: px2rem(25) px2rem(20);
    background-color: $shop-list-item-bg;
    border-radius: px2rem(10);
    cursor: pointer;
    height: 142px;
    overflow: hidden;

    @include bp(medium1) {
        height: 235px;
    }

    @include bp(large2) {
        height: 220px;
    }

    .shop-list-item-loyalty & {
        height: 300px;

        @include bp(medium1) {
            height: 500px;
        }

        @include bp(large2) {
            height: 470px;
        }

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 35px;
            background: linear-gradient(to top, $shop-list-item-bg 20%, transparent 100%);
            border-bottom-left-radius: px2rem(10);
            border-bottom-right-radius: px2rem(10);
        }
    }
}

.loyalty-program-info {
    h3 {
        margin: 10px 0 25px;
        padding: 0;
        text-align: center;

        @include body2-extrabold;

        @include bp(medium1) {
            @include body1-extrabold;
        }

        @include bp(large2) {
            @include h3-extrabold;

            margin: 10px 0 40px;
        }
    }

    ul {
        @include custom-unordered-list-style('small');
    }
}

.shop-list-benefit-icon {
    position: absolute;
    inset-block-start: px2rem(10);
    inset-inline-start: px2rem(10);
    width: px2rem(20);
}

.shop-list-item-thumbnail {
    width: 80px;
    height: 80px;
    aspect-ratio: 1/1;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;

    @include bp(medium1) {
        width: 140px;
        height: 140px;
    }

  img {
    object-fit: contain;
    mix-blend-mode: multiply;
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
  }
}
</style>
